import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Tooltip,
  Grid,
  Card,
  CardContent,
  Fab,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { vouchersService } from '../services/api';
import VoucherFormModal from './VoucherFormModal';

const UserVouchersModal = ({ open, onClose, user }) => {
  const [vouchers, setVouchers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [editingVoucher, setEditingVoucher] = useState(null);

  useEffect(() => {
    if (open && user) {
      fetchVouchers();
      fetchStats();
    }
  }, [open, user]);

  const fetchVouchers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await vouchersService.getUserVouchers(user.id);
      setVouchers(response.data);
    } catch (err) {
      console.error('Erreur lors du chargement des bons:', err);
      setError('Impossible de charger les bons');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await vouchersService.getStats(user.id);
      setStats(response.data);
    } catch (err) {
      console.error('Erreur lors du chargement des statistiques:', err);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce bon ?')) {
      try {
        await vouchersService.delete(id);
        fetchVouchers();
        fetchStats();
      } catch (err) {
        console.error('Erreur lors de la suppression:', err);
        setError('Impossible de supprimer le bon');
      }
    }
  };

  const handleEdit = (voucher) => {
    setEditingVoucher(voucher);
    setFormModalOpen(true);
  };

  const handleAddNew = () => {
    setEditingVoucher(null);
    setFormModalOpen(true);
  };

  const handleFormClose = (refresh) => {
    setFormModalOpen(false);
    setEditingVoucher(null);
    if (refresh) {
      fetchVouchers();
      fetchStats();
    }
  };

  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code);
  };

  const getTypeLabel = (type) => {
    const types = {
      percentage: 'Pourcentage',
      fixed: 'Montant fixe',
      free_product: 'Produit offert',
      free_shipping: 'Livraison gratuite',
    };
    return types[type] || type;
  };

  const getTypeColor = (type) => {
    const colors = {
      percentage: 'primary',
      fixed: 'success',
      free_product: 'warning',
      free_shipping: 'info',
    };
    return colors[type] || 'default';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    }).format(date);
  };

  const isExpired = (voucher) => {
    if (!voucher.validUntil) return false;
    return new Date(voucher.validUntil) < new Date();
  };

  const isNotYetValid = (voucher) => {
    if (!voucher.validFrom) return false;
    return new Date(voucher.validFrom) > new Date();
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Bons de réduction - {user?.username || user?.email}
            </Typography>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}

              {/* Statistiques */}
              {stats && (
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={3}>
                    <Card>
                      <CardContent>
                        <Typography variant="body2" color="text.secondary">
                          Total
                        </Typography>
                        <Typography variant="h4">{stats.total}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={3}>
                    <Card>
                      <CardContent>
                        <Typography variant="body2" color="text.secondary">
                          Actifs
                        </Typography>
                        <Typography variant="h4" color="success.main">
                          {stats.active}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={3}>
                    <Card>
                      <CardContent>
                        <Typography variant="body2" color="text.secondary">
                          Utilisés
                        </Typography>
                        <Typography variant="h4" color="primary.main">
                          {stats.used}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={3}>
                    <Card>
                      <CardContent>
                        <Typography variant="body2" color="text.secondary">
                          Expirés
                        </Typography>
                        <Typography variant="h4" color="error.main">
                          {stats.expired}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              )}

              {/* Liste des bons */}
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Code</TableCell>
                      <TableCell>Nom</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Valeur</TableCell>
                      <TableCell>Validité</TableCell>
                      <TableCell>Statut</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {vouchers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                            Aucun bon de réduction
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      vouchers.map((voucher) => (
                        <TableRow key={voucher.id} hover>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Chip
                                label={voucher.code}
                                size="small"
                                variant="outlined"
                                sx={{ fontFamily: 'monospace' }}
                              />
                              <IconButton size="small" onClick={() => handleCopyCode(voucher.code)}>
                                <CopyIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {voucher.name}
                            </Typography>
                            {voucher.reason && (
                              <Typography variant="caption" color="text.secondary">
                                {voucher.reason}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={getTypeLabel(voucher.type)}
                              size="small"
                              color={getTypeColor(voucher.type)}
                            />
                          </TableCell>
                          <TableCell>
                            {voucher.type === 'percentage' && `${voucher.discountValue}%`}
                            {voucher.type === 'fixed' && `${voucher.discountValue}€`}
                            {voucher.type === 'free_product' && 'Produit offert'}
                            {voucher.type === 'free_shipping' && 'Livraison offerte'}
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" display="block">
                              Du: {formatDate(voucher.validFrom)}
                            </Typography>
                            <Typography variant="caption" display="block">
                              Au: {formatDate(voucher.validUntil)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              {voucher.isUsed ? (
                                <Chip label="Utilisé" size="small" color="default" />
                              ) : isExpired(voucher) ? (
                                <Chip label="Expiré" size="small" color="error" />
                              ) : isNotYetValid(voucher) ? (
                                <Chip label="Pas encore valide" size="small" color="warning" />
                              ) : (
                                <Chip label="Actif" size="small" color="success" />
                              )}
                              {voucher.isCombinable ? (
                                <Chip
                                  label="📎 Combinable"
                                  size="small"
                                  variant="outlined"
                                  color="info"
                                  sx={{ fontSize: '0.7rem' }}
                                />
                              ) : (
                                <Chip
                                  label="🔒 Non combinable"
                                  size="small"
                                  variant="outlined"
                                  color="warning"
                                  sx={{ fontSize: '0.7rem' }}
                                />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            {!voucher.isUsed && (
                              <>
                                <Tooltip title="Modifier">
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => handleEdit(voucher)}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Supprimer">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleDelete(voucher.id)}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Bouton pour ajouter */}
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={handleAddNew}
                >
                  Ajouter un bon
                </Button>
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de formulaire */}
      <VoucherFormModal
        open={formModalOpen}
        onClose={handleFormClose}
        voucher={editingVoucher}
        userId={user?.id}
      />
    </>
  );
};

export default UserVouchersModal;

